import React, { useState, useCallback } from "react";
import { View, StyleSheet, Pressable, ActivityIndicator } from "react-native";
import { Menu } from "react-native-paper";
import { useNavigation } from "@react-navigation/native";
import type { NavigationProp } from "@react-navigation/native";
import CustomText from "../common/general/CustomText/CustomText";
import Pill from "../common/Pill/Pill";
import { CaretDown } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretUp } from "@phosphor-icons/react/dist/csr/CaretUp";
import { GestureResponderEvent } from "react-native";
import type { RootStackParamList } from "../../navigation/navigation.types";

interface Student {
  id: string;
  name: string;
  email: string;
  enrollmentDate: string;
  pendingCourseEnrollmentInvite?: boolean;
  expiredInvitation?: boolean;
  roles?: string[];
  analytics?: {
    success_points: number;
    confusion_points: number;
    questions_asked: number;
    active_interaction_count: number;
    distractions: number;
    sentiment?: string | null;
    completion_percentage?: number | null;
    total_time_taken?: number | null;
  } | null;
}

interface AdminRosterManagementTableProps {
  students: Student[];
  onResendInvite: (studentName: string, studentEmail: string) => void;
  onRemoveFromCourse: (studentId: string, studentName: string) => void;
  loading: boolean;
}

type MenuState = {
  visible: boolean;
  x: number;
  y: number;
  studentId: string;
  studentName: string;
  studentEmail: string;
  expiredInvitation: boolean;
  pendingCourseEnrollmentInvite: boolean;
};

const AdminRosterManagementTable: React.FC<AdminRosterManagementTableProps> = ({
  students,
  onResendInvite,
  onRemoveFromCourse,
  loading,
}) => {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();

  const [menuState, setMenuState] = useState<MenuState>({
    visible: false,
    x: 0,
    y: 0,
    studentId: "",
    studentName: "",
    studentEmail: "",
    expiredInvitation: false,
    pendingCourseEnrollmentInvite: false,
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusPill = (student: Student) => {
    if (student.pendingCourseEnrollmentInvite) {
      if (student.expiredInvitation) {
        return { text: "EXPIRED", backgroundColor: "#FEF3C7", textColor: "#92400E" };
      }
      return { text: "PENDING", backgroundColor: "#FEF3C7", textColor: "#92400E" };
    }
    return { text: "ACTIVE", backgroundColor: "#D1FAE5", textColor: "#065F46" };
  };

  const formatProgress = (value?: number | null) => {
    if (value === null || value === undefined || isNaN(value)) return "—";
    const pct = value <= 1 ? value * 100 : value;
    const clamped = Math.max(0, Math.min(100, Math.round(pct)));
    return `${clamped}%`;
  };

  const getSentimentDisplay = (sentiment?: string | null) => {
    if (!sentiment) return { label: "—", color: "#374151" };
    const s = sentiment.toLowerCase();
    if (s.includes("pos")) return { label: "Positive", color: "#065F46" };
    if (s.includes("neg")) return { label: "Negative", color: "#B91C1C" };
    if (s.includes("mix") || s.includes("neu")) return { label: "Neutral", color: "#92400E" };
    return { label: sentiment, color: "#374151" };
  };

  const handleOpenMenu = useCallback((event: GestureResponderEvent, student: Student) => {
    const { pageX, pageY } = event.nativeEvent;
    setMenuState({
      visible: true,
      x: pageX - 100,
      y: pageY,
      studentId: student.id,
      studentName: student.name,
      studentEmail: student.email,
      expiredInvitation: student.expiredInvitation,
      pendingCourseEnrollmentInvite: student.pendingCourseEnrollmentInvite,
    });
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuState({
      visible: false,
      x: 0,
      y: 0,
      studentId: "",
      studentName: "",
      studentEmail: "",
      expiredInvitation: false,
      pendingCourseEnrollmentInvite: false,
    });
  }, []);

  const handleViewProfile = () => {
    handleCloseMenu();
    navigation.navigate("Student Profile", { studentId: menuState.studentId });
  };

  const handleViewGrades = () => {
    handleCloseMenu();
    // TODO: Implement view grades functionality
    console.log("View Grades for student:", menuState.studentId);
  };

  const handleSendMessage = () => {
    handleCloseMenu();
    // TODO: Implement send message functionality
    console.log("Send Message to student:", menuState.studentId);
  };

  const handleRemoveFromCourse = () => {
    handleCloseMenu();
    onRemoveFromCourse(menuState.studentId, menuState.studentName);
  };

  const handleResendInvite = async (studentName: string, studentEmail: string) => {
    handleCloseMenu();
    onResendInvite(studentName, studentEmail);
  };

  return (
    <>
      <View style={styles.tableContainer}>
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <View style={[styles.cell, styles.studentCell]}>
              <CustomText text="Student" weight="bold" style={styles.headerText} useTranslationText={false} />
            </View>
            <View style={[styles.cell, styles.emailCell]}>
              <CustomText text="Email" weight="bold" style={styles.headerText} useTranslationText={false} />
            </View>
            <View style={[styles.cell, styles.enrollmentDateCell]}>
              <CustomText text="Enrollment Date" weight="bold" style={styles.headerText} useTranslationText={false} />
            </View>
            <View style={[styles.cell, styles.progressCell]}>
              <CustomText text="Progress" weight="bold" style={styles.headerText} useTranslationText={false} />
            </View>
            <View style={[styles.cell, styles.sentimentCell]}>
              <CustomText text="Sentiment" weight="bold" style={styles.headerText} useTranslationText={false} />
            </View>
            <View style={[styles.cell, styles.statusCell]}>
              <CustomText text="Status" weight="bold" style={styles.headerText} useTranslationText={false} />
            </View>
            <View style={[styles.cell, styles.actionsCell]}>
              <CustomText text="Actions" weight="bold" style={styles.headerText} useTranslationText={false} />
            </View>
          </View>

          {/* Table Body */}
          {loading && (
            <View style={styles.loaderContainer}>
              <ActivityIndicator size="large" color="#0000ff" />
            </View>
          )}
          {!loading && students.length === 0 && (
            <View style={styles.loaderContainer}>
              <CustomText
                text={"No matching student records"}
                weight="bold"
                style={styles.emailText}
                useTranslationText={true}
              />
            </View>
          )}
          {!loading &&
            students.length > 0 &&
            students.map((student) => {
              const statusPill = getStatusPill(student);
              const isMenuOpen = menuState.visible && menuState.studentId === student.id;
              const initials = student.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .substring(0, 2);

              return (
                <View key={student.id} style={styles.tableRow}>
                  <View style={[styles.cell, styles.studentCell]}>
                    <View style={styles.studentInfo}>
                      <View style={styles.avatar}>
                        <CustomText
                          text={initials}
                          weight="bold"
                          style={styles.avatarText}
                          useTranslationText={false}
                        />
                      </View>
                      <CustomText
                        text={student.name}
                        size="s"
                        weight="medium"
                        style={styles.studentName}
                        useTranslationText={false}
                      />
                    </View>
                  </View>

                  <View style={[styles.cell, styles.emailCell]}>
                    <CustomText text={student.email} size="s" style={styles.emailText} useTranslationText={false} />
                  </View>

                  <View style={[styles.cell, styles.enrollmentDateCell]}>
                    <CustomText
                      text={formatDate(student.enrollmentDate)}
                      size="s"
                      style={styles.dateText}
                      useTranslationText={false}
                    />
                  </View>

                  <View style={[styles.cell, styles.progressCell]}>
                    <CustomText
                      text={formatProgress(student.analytics?.completion_percentage)}
                      size="s"
                      style={styles.progressText}
                      useTranslationText={false}
                    />
                  </View>

                  <View style={[styles.cell, styles.sentimentCell]}>
                    {(() => {
                      const sentiment = getSentimentDisplay(student.analytics?.sentiment ?? null);
                      return (
                        <Pressable
                          onPress={() => navigation.navigate("Admin Analytics", { studentId: student.id })}
                          style={styles.sentimentPressable}>
                          <CustomText
                            text={sentiment.label}
                            size="s"
                            style={[styles.sentimentText, { color: sentiment.color }]}
                            useTranslationText={false}
                          />
                        </Pressable>
                      );
                    })()}
                  </View>

                  <View style={[styles.cell, styles.statusCell]}>
                    <View style={styles.pillContainer}>
                      <Pill
                        text={statusPill.text}
                        backgroundColor={statusPill.backgroundColor}
                        textColor={statusPill.textColor}
                      />
                    </View>
                  </View>

                  <View style={[styles.cell, styles.actionsCell]}>
                    <Pressable
                      style={[styles.manageButton, isMenuOpen && styles.manageButtonActive]}
                      onPress={(e) => handleOpenMenu(e, student)}>
                      <CustomText
                        text="Manage"
                        size="s"
                        style={[styles.manageText, isMenuOpen && styles.manageTextActive]}
                        useTranslationText={false}
                      />
                      {isMenuOpen ? <CaretUp size={14} color="#FFFFFF" /> : <CaretDown size={14} color="#475569" />}
                    </Pressable>
                  </View>
                </View>
              );
            })}
        </View>
      </View>

      <Menu
        visible={menuState.visible}
        onDismiss={handleCloseMenu}
        anchor={{ x: menuState.x, y: menuState.y }}
        contentStyle={styles.menu}>
        <Menu.Item onPress={handleViewProfile} title="View Profile" titleStyle={styles.menuItemText} />
        <Menu.Item onPress={handleViewGrades} title="View Grades" titleStyle={styles.menuItemText} />
        <Menu.Item onPress={handleSendMessage} title="Send Message" titleStyle={styles.menuItemText} />
        <Menu.Item
          onPress={() => handleResendInvite(menuState.studentName, menuState.studentEmail)}
          title="Resend Invite"
          titleStyle={[
            styles.menuItemText,
            !menuState.expiredInvitation && !menuState.pendingCourseEnrollmentInvite && styles.disabledText,
          ]}
          disabled={!menuState.expiredInvitation && !menuState.pendingCourseEnrollmentInvite}
        />
        <View style={styles.menuDivider} />
        <Menu.Item
          onPress={handleRemoveFromCourse}
          title="Remove from Course"
          titleStyle={[styles.menuItemText, styles.deleteText]}
        />
      </Menu>
    </>
  );
};

const styles = StyleSheet.create({
  tableContainer: {
    flex: 1,
  },
  table: {
    backgroundColor: "#fff",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 72,
  },
  cell: {
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  studentCell: {
    flex: 3,
  },
  emailCell: {
    flex: 3,
  },
  enrollmentDateCell: {
    flex: 2,
  },
  progressCell: {
    flex: 1.5,
  },
  sentimentCell: {
    flex: 1.5,
  },
  statusCell: {
    flex: 1.5,
    alignItems: "flex-start",
  },
  actionsCell: {
    flex: 1.25,
    alignItems: "center",
  },
  headerText: {
    color: "#475569",
    fontSize: 14,
    letterSpacing: 0.5,
  },
  studentInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    backgroundColor: "#3E68FE",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: "#FFFFFF",
    fontSize: 16,
  },
  studentName: {
    color: "#101828",
  },
  emailText: {
    color: "#101828",
  },
  dateText: {
    color: "#101828",
  },
  progressText: {
    color: "#101828",
  },
  sentimentText: {
    color: "#374151",
  },
  sentimentPressable: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    cursor: "pointer",
  },
  pillContainer: {
    alignSelf: "flex-start",
  },
  manageButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    backgroundColor: "#F9FAFB",
  },
  manageButtonActive: {
    backgroundColor: "#3E68FE",
    borderColor: "#3E68FE",
  },
  manageText: {
    color: "#475569",
    fontSize: 13,
  },
  manageTextActive: {
    color: "#FFFFFF",
  },
  menu: {
    backgroundColor: "#FFFFFF",
    minWidth: 200,
  },
  menuItemText: {
    fontSize: 14,
    color: "#374151",
  },
  deleteText: {
    color: "#DC2626",
  },
  menuDivider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginVertical: 4,
  },
  disabledText: {
    color: "#9CA3AF",
  },
  loaderContainer: {
    flex: 3,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
});

export default AdminRosterManagementTable;
